import { BaseService } from './baseService';
import { apiClient } from '../config/api';
import type { Parcela } from '../types/entities';

export interface ParcelaSearchParams {
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: {
    pago?: boolean;
    dataVencimento?: {
      gte?: string;
      lte?: string;
      gt?: string;
      lt?: string;
    };
    dataPagamento?: {
      gte?: string;
      lte?: string;
      gt?: string;
      lt?: string;
    };
    statusId?: string;
    situacaoId?: string;
    seguradoraId?: string;
    produtoId?: string;
    empresaId?: string;
  };
}

export interface ParcelaData extends Parcela {
  // Dados relacionados que podem vir do backend
  documento?: {
    id: string;
    numeroDocumento: string;
    // Dados básicos para tabela
    cliente?: {
      nome: string;
      cpfCnpj: string;
    };
    status?: {
      id: string;
      nome: string;
    };
    situacao?: {
      id: string;
      nome: string;
    };
    seguradora?: {
      id: string;
      nome: string;
    };
    produto?: {
      id: string;
      nome: string;
    };
    // Dados completos para ApoliceFormModal (quando includeFullDocument = true)
    fullData?: any; // Dados completos do documento/apólice
  };
}

class ParcelaListService extends BaseService<ParcelaData> {
  protected endpoints = {
    LIST: '/parcelas',
    CREATE: '/parcelas',
    SEARCH: '/parcelas',
    BY_ID: (id: string | number) => `/parcelas/${id}`,
    UPDATE: (id: string | number) => `/parcelas/${id}`,
    DELETE: (id: string | number) => `/parcelas/${id}`,
  };

  async getParcelas(params: ParcelaSearchParams, endpoint = '/parcelas', includeFullDocument = false) {
    const queryParams = new URLSearchParams();

    // Basic params
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    
    // Include full document data for navigation
    if (includeFullDocument) {
      queryParams.append('include', 'documento');
      // Pode incluir mais dados relacionados se necessário
      queryParams.append('expand', 'documento.cliente,documento.seguradora,documento.produto,documento.status,documento.situacao,documento.parcelas,documento.repasses');
    }

    // Filters
    if (params.filters) {
      // Boolean filter for pago
      if (typeof params.filters.pago === 'boolean') {
        queryParams.append('filters[pago]', params.filters.pago.toString());
      }

      // Date range filters
      if (params.filters.dataVencimento) {
        Object.entries(params.filters.dataVencimento).forEach(([operator, value]) => {
          if (value) {
            queryParams.append(`filters[dataVencimento][${operator}]`, value);
          }
        });
      }

      if (params.filters.dataPagamento) {
        Object.entries(params.filters.dataPagamento).forEach(([operator, value]) => {
          if (value) {
            queryParams.append(`filters[dataPagamento][${operator}]`, value);
          }
        });
      }

      // Other filters
      if (params.filters.statusId) {
        queryParams.append('filters[statusId]', params.filters.statusId);
      }
      if (params.filters.situacaoId) {
        queryParams.append('filters[situacaoId]', params.filters.situacaoId);
      }
      if (params.filters.seguradoraId) {
        queryParams.append('filters[seguradoraId]', params.filters.seguradoraId);
      }
      if (params.filters.produtoId) {
        queryParams.append('filters[produtoId]', params.filters.produtoId);
      }
      if (params.filters.empresaId) {
        queryParams.append('filters[empresaId]', params.filters.empresaId);
      }
    }

    const url = `${endpoint}?${queryParams.toString()}`;
    
    return apiClient.request<any>(url);
  }
}

export const parcelaListService = new ParcelaListService();
export { ParcelaListService };